import React, { useState, useRef } from 'react';
import { useProfileState } from 'context/profile';
import { toast } from 'components/Shared/Toast';
import { useTranslation } from 'react-i18next';
import { useQueryClient, useMutation } from 'react-query';
import { fetcher } from 'api/client';
import { registerTwoFactor } from 'api/actions/users';
import { changePassword, generateTwoFactor, verifyTwoFactor, disableTwoFactor } from 'api/actions/auth';
import clsx from 'clsx';
import produce from 'immer';
import { Badge, Button, ButtonGroup, Col, FormGroup, Input, InputGroup, ListGroup, ListGroupItem, Row } from 'reactstrap';

const initialState = {
    min_chars: false,
    hasLower: false,
    min_digit: false,
    min_upper: false,
    is_equals: false,
    isValid: false
}

function SecurityContainer() {
    const { t } = useTranslation();
    const queryClient = useQueryClient();
    const { profile } = useProfileState();
    const [twoFactor, settwoFactor] = useState({});
    const [validations, setvalidations] = useState(initialState)
    const password = useRef(null);
    const passwordConfirm = useRef(null);
    const currentPassword = useRef(null);
    const token = useRef(null);

    const validate = () => setvalidations(produce(draft => {
        draft.min_chars = /([\s\S]{8,})/.test(password.current.value);
        draft.min_upper = /[A-Z]/.test(password.current.value);
        draft.min_digit = /[0-9]/.test(password.current.value);
        draft.hasLower = /(.*[a-z].*)/.test(password.current.value);
        draft.is_equals = password.current.value === passwordConfirm.current.value;
        draft.isValid = draft.min_chars && draft.min_upper && draft.min_digit && draft.is_equals && draft.hasLower;
    }))

    const { mutate: mutatePassword } = useMutation((payload) => fetcher(changePassword(payload),), {
        onSuccess: () => {
            toast.success(t('profile.personal.password_success_change'));
            setvalidations(initialState);
            password.current.value = '';
            passwordConfirm.current.value = '';
            currentPassword.current.value = '';
        },
        onError: (error) => {
            toast.error(t(`errors.${error.message}`, t('errors.GeneralError')))
        }
    });

    const { mutate: mutateVerifyTwoFactor } = useMutation((data) => fetcher(verifyTwoFactor(data),), {
        onSuccess: (data) => {
            queryClient.invalidateQueries('profile');
        }, onError: (error) => {
            toast.error(t(`errors.${error.message}`, t('errors.GeneralError')))
        }
    });

    const { mutate: mutateDisableMutateTwoFactor } = useMutation(() => fetcher(disableTwoFactor(),), {
        onSuccess: (data) => {
            toast.success(t('profile.personal.two_factor_disabled'));
            queryClient.invalidateQueries('profile');
        }
    });

    const { mutate: mutateGenerateTwoFactor } = useMutation(() => fetcher(generateTwoFactor(),), {
        onSuccess: ({ data }) => {
            settwoFactor({
                setup2fa: true,
                qrcode: data.url,
                secret2fa: data.secret
            });
        }
    });

    const handleRegisterTwoFactor = () => {
        mutateVerifyTwoFactor({ token: token.current.value })
    }

    const handleDisableMutateTwoFactor = () => {
        mutateDisableMutateTwoFactor();
    }

    const handleMutateTwoFactor = () => {
        mutateGenerateTwoFactor();
    }

    const savePassword = () => {
        if (validations.isValid) mutatePassword({ previousPassword: currentPassword.current.value, newPassword: password.current.value })
    }


    return (
        <div>
            <h5><i className="fas fa-key"></i> {t('profile.security.password')}</h5>
            <Row>
                <Col md="3">
                    <FormGroup>
                        <label>{t('profile.security.current_password')}:</label>
                        <input ref={currentPassword} type="password" className="form-control" placeholder={t('profile.security.new_password_placeholder')} autoComplete="new-name" name="current-password" />
                    </FormGroup>
                </Col>
                <Col md="3">
                    <FormGroup>
                        <label>{t('profile.security.new_password')}:</label>
                        <input onKeyUp={validate} ref={password} type="password" className="form-control" placeholder={t('profile.security.new_password_placeholder')} autoComplete="new-name" name="password" />
                    </FormGroup>
                </Col>
                <Col md="3">
                    <FormGroup>
                        <label>{t('profile.security.confirm_password')}:</label>
                        <input onKeyUp={validate} ref={passwordConfirm} type="password" className="form-control" placeholder={t('profile.security.confirm_password_placeholder')} autoComplete="new-name" name="password-confirm" />
                    </FormGroup>
                </Col>
                <Col md="3" className="text-left">
                    <FormGroup>
                        <label>&#8192;</label>
                        <Button color="secondary" block onClick={savePassword} disabled={!validations.isValid}>
                            {t('profile.security.password_save')}
                        </Button>
                    </FormGroup>
                </Col>
                <Col>
                    <ListGroup>
                        <ListGroupItem>
                            <i className={clsx('mr-2 fas fa-circle', validations.min_chars ? 'text-success' : 'text-muted')}></i>
                            {t('profile.security.min_chars')}
                        </ListGroupItem>
                        <ListGroupItem>
                            <i className={clsx('mr-2 fas fa-circle', validations.min_digit ? 'text-success' : 'text-muted')}></i>
                            {t('profile.security.min_digit')}
                        </ListGroupItem>
                        <ListGroupItem>
                            <i className={clsx('mr-2 fas fa-circle', validations.min_upper ? 'text-success' : 'text-muted')}></i>
                            {t('profile.security.min_upper')}
                        </ListGroupItem>
                        <ListGroupItem>
                            <i className={clsx('mr-2 fas fa-circle', validations.is_equals ? 'text-success' : 'text-muted')}></i>
                            {t('profile.security.is_equals')}
                        </ListGroupItem>
                        <ListGroupItem>
                            <i className={clsx('mr-2 fas fa-circle', validations.hasLower ? 'text-success' : 'text-muted')}></i>
                            {t('profile.security.has_lower')}
                        </ListGroupItem>
                    </ListGroup>
                </Col>
            </Row>

            <hr />
            <h5 className="d-inline-block my-3 mr-2"><i className="fas fa-lock"></i> {t('profile.security.twofactor')}</h5>

            {profile.twoFactor?.enabled ? <Badge color="success">{t('profile.security.twofactor_enabled')}</Badge>
                : <Badge color="danger">{t('profile.security.twofactor_disabled')}</Badge>
            }
            <Row>
                <Col md="12">
                    <p className="text-justify">
                        {t('profile.security.twofactor_description')}
                    </p>
                    {/* <a href="#" data-action="cancel-2fa">{t('profile.security.twofactor_cancel')}</a> */}
                    <div className={clsx('mt-2', twoFactor.secret2fa || 'd-none', profile.twoFactor?.enabled && 'd-none')}>
                        {t('profile.security.twofactor_step1')}
                        <ol>
                            <li className="my-1">
                                {t('profile.security.twofactor_step2')}
                                <ul>
                                    <li className="my-1"><a href="https://play.google.com/store/apps/details?id=com.google.android.apps.authenticator2">Google Authenticator - Android</a></li>
                                    <li className="my-1"><a href=" https://itunes.apple.com/ca/app/google-authenticator/id388497605?mt=8">Google Authenticator - iOS</a></li>
                                </ul>
                            </li>
                            <li className="my-1">{t('profile.security.twofactor_step3')}.
                            <div className="text-center mb-3">
                                    <img className="twofa-code" src={twoFactor.qrcode} alt="qr" />
                                    <div></div>
                                    <Badge color="light" className="p-3">{twoFactor.secret2fa}</Badge>
                                </div>
                            </li>
                            <li className="my-1">{t('profile.security.twofactor_step4')}</li>
                        </ol>
                        <Row>
                            <Col md="12" >
                                <InputGroup className="text-center">
                                    <Input innerRef={token} placeholder={t('profile.security.twofactor_code')} maxLength="6" minLength="6" length="6" name="token-2fa" />
                                    <Button color="primary" onClick={handleRegisterTwoFactor}>{t('profile.security.twofactor_send')}</Button>
                                </InputGroup>
                            </Col>
                        </Row>
                    </div>

                    {profile.twoFactor?.enabled ?
                        <Button color="secondary" block onClick={handleDisableMutateTwoFactor} >{t('profile.security.twofactor_setdisabled')}</Button>
                        :
                        <Button color="secondary" block hidden={twoFactor.secret2fa} onClick={handleMutateTwoFactor}>{t('profile.security.twofactor_setenabled')}</Button>
                    }
                    {/* <a href="#" className="not-allowed" title={t('wordList.plans.availablePE')}>{t('profile.security.twofactor_setenabled')}</a> */}

                </Col>
            </Row>
            <div className="divider"></div>

        </div>
    )
}

export default SecurityContainer;